import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';

// Public pages
import PublicLayout   from './components/public/PublicLayout';
import HomePage       from './pages/public/HomePage';
import CSOsPage       from './pages/public/CSOsPage';
import CSOPublicProfile from './pages/public/CSOPublicProfile';
import AboutPage      from './pages/public/AboutPage';
import ResourcesPage  from './pages/public/ResourcesPage';
import ContactPage    from './pages/public/ContactPage';
import NewsPage       from './pages/public/NewsPage';
import NewsArticle    from './pages/public/NewsArticle';
import ProvincesPage  from './pages/public/ProvincesPage';

// Admin pages
import AdminLogin     from './pages/admin/AdminLogin';
import AdminLayout    from './components/admin/AdminLayout';
import Dashboard      from './pages/admin/Dashboard';
import OrgsList       from './pages/admin/OrgsList';
import OrgForm        from './pages/admin/OrgForm';
import OrgDetail      from './pages/admin/OrgDetail';
import UsersPage      from './pages/admin/UsersPage';
import AdminNews      from './pages/admin/AdminNews';
import AdminResources from './pages/admin/AdminResources';
import MediaLibrary   from './pages/admin/MediaLibrary';
import AuditLogs      from './pages/admin/AuditLogs';
import SettingsPage   from './pages/admin/SettingsPage';
import ProvincesAdmin from './pages/admin/ProvincesAdmin';
import DistrictsAdmin from './pages/admin/DistrictsAdmin';
import ThematicAdmin  from './pages/admin/ThematicAdmin';
import ContactInbox   from './pages/admin/ContactInbox';
import ReportsPage    from './pages/admin/ReportsPage';

const STAFF = ['super_admin','admin','data_entry','field_officer'];

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-center"><div className="spin"/></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" replace />;
  return children;
}

function AdminRoute({ children, roles }) {
  return (
    <PrivateRoute roles={roles || STAFF}>
      <AdminLayout>{children}</AdminLayout>
    </PrivateRoute>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* ── Public ── */}
          <Route path="/" element={<PublicLayout><HomePage/></PublicLayout>}/>
          <Route path="/csos" element={<PublicLayout><CSOsPage/></PublicLayout>}/>
          <Route path="/csos/:slug" element={<PublicLayout><CSOPublicProfile/></PublicLayout>}/>
          <Route path="/about" element={<PublicLayout><AboutPage/></PublicLayout>}/>
          <Route path="/resources" element={<PublicLayout><ResourcesPage/></PublicLayout>}/>
          <Route path="/contact" element={<PublicLayout><ContactPage/></PublicLayout>}/>
          <Route path="/news" element={<PublicLayout><NewsPage/></PublicLayout>}/>
          <Route path="/news/:slug" element={<PublicLayout><NewsArticle/></PublicLayout>}/>
          <Route path="/provinces" element={<PublicLayout><ProvincesPage/></PublicLayout>}/>

          {/* ── Admin Auth ── */}
          <Route path="/admin/login" element={<AdminLogin/>}/>

          {/* ── Admin Panel ── */}
          <Route path="/admin"             element={<AdminRoute><Dashboard/></AdminRoute>}/>
          <Route path="/admin/organisations" element={<AdminRoute><OrgsList/></AdminRoute>}/>
          <Route path="/admin/organisations/new" element={<AdminRoute roles={['super_admin','admin','data_entry']}><OrgForm/></AdminRoute>}/>
          <Route path="/admin/organisations/edit/:id" element={<AdminRoute roles={['super_admin','admin','data_entry']}><OrgForm/></AdminRoute>}/>
          <Route path="/admin/organisations/:id" element={<AdminRoute><OrgDetail/></AdminRoute>}/>
          <Route path="/admin/users"       element={<AdminRoute roles={['super_admin','admin']}><UsersPage/></AdminRoute>}/>
          <Route path="/admin/news"        element={<AdminRoute roles={['super_admin','admin']}><AdminNews/></AdminRoute>}/>
          <Route path="/admin/resources"   element={<AdminRoute roles={['super_admin','admin']}><AdminResources/></AdminRoute>}/>
          <Route path="/admin/media"       element={<AdminRoute><MediaLibrary/></AdminRoute>}/>
          <Route path="/admin/provinces"   element={<AdminRoute roles={['super_admin','admin']}><ProvincesAdmin/></AdminRoute>}/>
          <Route path="/admin/districts"   element={<AdminRoute roles={['super_admin','admin']}><DistrictsAdmin/></AdminRoute>}/>
          <Route path="/admin/thematic"    element={<AdminRoute roles={['super_admin','admin']}><ThematicAdmin/></AdminRoute>}/>
          <Route path="/admin/reports"     element={<AdminRoute><ReportsPage/></AdminRoute>}/>
          <Route path="/admin/audit"       element={<AdminRoute roles={['super_admin','admin']}><AuditLogs/></AdminRoute>}/>
          <Route path="/admin/settings"    element={<AdminRoute roles={['super_admin']}><SettingsPage/></AdminRoute>}/>
          <Route path="/admin/contact"     element={<AdminRoute roles={['super_admin','admin']}><ContactInbox/></AdminRoute>}/>
          <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
      </BrowserRouter>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false}/>
    </AuthProvider>
  );
}
